import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Popconfirm,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import { GroupProps, RoleProps, UserProps } from "../../interfaces/interfaces";
import { ColumnComponent } from "../../components/ui";
import { MultipleSelect, SearchInput } from "../../components/functional";
import {
  DownloadOutlined,
  UserAddOutlined,
  DeleteOutlined,
  EditOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { ModalGroupForm, ModalUserForm } from "../../components/user";
import {
  GROUP_INITIAL_STATE,
  MODAL_INITIAL_STATE,
  USER_INITIAL_STATE,
} from "../../utils/data";
import DataContext from "../../context/DataContext";
import AuthContext from "../../context/AuthContext";
import { useDemoModal } from "../../hooks";
import dayjs from "dayjs";
import { exportUserList } from "../../utils/func/ExportFile";

export const UserListPage = () => {
  const { idClient } = useParams();
  const {
    usersList,
    dataLoading,
    userGroups,
    getData,
    handleEditData,
    handleCreateData,
  } = useContext(DataContext);
  const { isDemoEnv, profileInfo } = useContext(AuthContext);
  const { DemoModal, openModalDemo } = useDemoModal();
  const [modalStatus, setModalStatus] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    multiple: boolean;
    payload?: UserProps | null;
  }>(MODAL_INITIAL_STATE);
  const [modalGroupStatus, setModalGroupStatus] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    multiple: boolean;
    payload?: GroupProps | null;
  }>(MODAL_INITIAL_STATE);
  const [activeKey, setActiveKey] = useState<string>("1");

  useEffect(() => {
    if (idClient) {
      getData("users", idClient);
      getData("groups", idClient);
    }
  }, [idClient]);

  const createOrUpdateUser = (
    values: UserProps,
    idClient: string,
    idUser?: string,
  ) => {
    if (modalStatus.create) {
      handleCreateData({
        endpoint: "user",
        idClient,
        data: values,
        next: () => {
          getData("users", idClient);
          getData("groups", idClient);
        },
      });
    } else if (modalStatus.update && idUser) {
      handleEditData({
        endpoint: `user/${idClient}`,
        newData: { ...values, _id: idUser },
        next: () => {
          getData("users", idClient);
          getData("groups", idClient);
        },
      });
    }
  };

  const createOrUpdateGroup = (values: GroupProps, idClient: string) => {
    if (modalGroupStatus.create) {
      handleCreateData({
        endpoint: "groups",
        idClient,
        data: values,
        next: () => {
          getData("users", idClient);
          getData("groups", idClient);
        },
      });
    } else if (modalGroupStatus.update) {
      handleEditData({
        endpoint: `group/${idClient}/${values._id}`,
        newData: values,
        next: () => {
          getData("users", idClient);
          getData("groups", idClient);
        },
      });
    }
  };

  const columns: ColumnsType<UserProps> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",

      onCell: (record, index) => {
        return {
          onClick: () =>
            setModalStatus({
              open: true,
              create: false,
              update: true,
              multiple: false,
              payload: record,
            }),
        };
      },
    },
    {
      title: "Email",
      dataIndex: "username",
      key: "email",
      width: "15%",
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "10%",
      render: (role: RoleProps) => role?.description,
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      width: "10%",
      render: (lastLogin: Date) =>
        lastLogin ? dayjs(lastLogin).format("MM/DD/YYYY HH:mm") : "",
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Groups",
      dataIndex: "groupList",
      key: "groupList",
      width: "20%",
      render: (groupList: GroupProps[]) =>
        groupList?.map((group) => (
          <Tag color="blue" key={group._id}>
            {group.name}
          </Tag>
        )),

      responsive: ["xl", "lg", "md"],
    },

    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "5%",
      render: (_, user) => (
        <Space direction="horizontal" size={0} style={{ width: "100%" }}>
          <Popconfirm
            disabled={user.username === profileInfo?.username}
            title={
              user.active
                ? "This action will deactivate the user. Are you sure?"
                : "This action will activate the user. Are you sure?"
            }
            onConfirm={() => {
              if (isDemoEnv) {
                openModalDemo();
              } else {
                idClient &&
                  handleEditData({
                    endpoint: `user/${idClient}`,
                    newData: { active: !user.active, _id: user._id },
                    next: () => getData("users", idClient),
                  });
              }
            }}
          >
            <Checkbox
              checked={user.active}
              disabled={user.username === profileInfo?.username}
            />
          </Popconfirm>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() =>
              setModalStatus({
                open: true,
                create: false,
                update: true,
                multiple: false,
                payload: user,
              })
            }
          ></Button>
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              if (isDemoEnv) {
                openModalDemo();
              } else {
                idClient &&
                  handleEditData({
                    endpoint: `user/${idClient}`,
                    newData: { deleteFromClient: true, _id: user._id },
                    next: () => {
                      getData("users", idClient);
                      getData("groups", idClient);
                    },
                  });
              }
            }}
          >
            <Tooltip title="Remove User" placement="left">
              <Button
                icon={<DeleteOutlined />}
                disabled={user.username === profileInfo?.username}
                type="link"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const groupColumns: ColumnsType<GroupProps> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      onCell: (record, index) => {
        return {
          onClick: () =>
            setModalGroupStatus({
              open: true,
              create: false,
              update: true,
              multiple: false,
              payload: record,
            }),
        };
      },
    },
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      width: "40%",
      render: (users: UserProps[]) =>
        users?.map((user) => (
          <Tag color="blue" key={user._id}>
            {user.name}
          </Tag>
        )),
      responsive: ["xl", "lg", "md"],
    },

    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: "30%",
      render: (_, row) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() =>
            setModalGroupStatus({
              open: true,
              create: false,
              update: true,
              multiple: false,
              payload: row,
            })
          }
        ></Button>
      ),
    },
  ];

  const items = [
    {
      key: "1",
      label: `Users (${usersList?.length})`,
      children: (
        <Table
          loading={dataLoading}
          bordered
          size="small"
          columns={columns}
          dataSource={usersList}
          rowKey="_id"
          scroll={{ y: "70vh", x: "80%" }}
          tableLayout="auto"
        />
      ),
    },
    {
      key: "2",
      label: `Groups (${userGroups?.length})`,
      children: (
        <Table
          loading={dataLoading}
          bordered
          size="small"
          columns={groupColumns}
          dataSource={userGroups}
          rowKey="_id"
          scroll={{ y: "70vh", x: "80%" }}
          tableLayout="auto"
        />
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <div>
            <Card
              className="summary-client__card"
              styles={{ body: { display: "none" } }}
              actions={[
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Filter By Group</label>
                  <Tooltip title="Filter By Group">
                    <MultipleSelect
                      placeholder="Group"
                      className="summary-client__card__actions__select"
                      options={userGroups.map((group) => ({
                        label: group.name,
                        value: group._id,
                      }))}
                      onSelect={(values) => {
                        if (values.length === 0 && idClient) {
                          activeKey === "1"
                            ? getData("users", idClient)
                            : getData("groups", idClient);
                          return;
                        }
                        if (idClient)
                          activeKey === "1"
                            ? getData("users", idClient, undefined, {
                                groupList: {
                                  $in: values,
                                },
                              })
                            : // Filter groups by users
                              getData("groups", idClient, undefined, {
                                _id: {
                                  $in: values,
                                },
                              });
                      }}
                    />
                  </Tooltip>
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Search</label>
                  <SearchInput
                    onSearch={(value: string) => {
                      if (value) {
                        idClient &&
                          getData(
                            activeKey === "1" ? "users" : "groups",
                            idClient,
                            value,
                          );
                      } else {
                        idClient &&
                          getData(
                            activeKey === "1" ? "users" : "groups",
                            idClient,
                          );
                      }
                    }}
                  />
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Actions</label>
                  <Space direction="horizontal" size={0}>
                    <Tooltip title="Create new User" placement="left">
                      <Button
                        type="link"
                        icon={<UserAddOutlined />}
                        size="large"
                        onClick={() => {
                          setModalStatus({
                            open: true,
                            create: true,
                            update: false,
                            multiple: false,
                            payload: USER_INITIAL_STATE,
                          });
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Create new Group" placement="top">
                      <Button
                        type="link"
                        icon={<UsergroupAddOutlined />}
                        size="large"
                        onClick={() => {
                          setModalGroupStatus({
                            open: true,
                            create: true,
                            update: false,
                            multiple: false,
                            payload: GROUP_INITIAL_STATE,
                          });
                        }}
                      />
                    </Tooltip>
                    <Tooltip title={"Export"} placement="right">
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={() => exportUserList(usersList, [])}
                      />
                    </Tooltip>
                  </Space>
                </Space>,
              ]}
            ></Card>
          </div>
        </ColumnComponent>
        <ColumnComponent>
          <Tabs
            style={{ padding: "0 12px" }}
            items={items}
            activeKey={activeKey}
            onChange={(key) => setActiveKey(key)}
          />
        </ColumnComponent>
      </Row>
      <ModalUserForm
        modalStatus={modalStatus}
        onCancel={() => setModalStatus(MODAL_INITIAL_STATE)}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (idClient) {
              createOrUpdateUser(values, idClient, modalStatus.payload?._id);
              setModalStatus(MODAL_INITIAL_STATE);
            }
          }
        }}
      />
      <ModalGroupForm
        modalStatus={modalGroupStatus}
        onCancel={() => setModalGroupStatus(MODAL_INITIAL_STATE)}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (idClient) {
              createOrUpdateGroup(values, idClient);
              setModalGroupStatus(MODAL_INITIAL_STATE);
            }
          }
        }}
      />
      <DemoModal />
    </>
  );
};
